import React, { useEffect, useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  Tabs,
  Table,
  Button,
  CustomGrid,
  Input,
  Modal,
  Checkbox,
  ColorPicker,
} from 'ui';
import dayjs from 'dayjs';
import { StyledButtonList } from 'styles/buttonList';
import { Form, Upload } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import RichTextInput from '../../../../ui/Input/ReactQuillInput';
import {
  QuillGlobal,
  StyledCustomSuffix,
  StyledUploadLogoModal,
} from '../style';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile } from 'antd/es/upload/interface';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

const SettingListPage = () => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<number>(0);
  const [reload, setReload] = useState<number>(0);
  const [isModalVisiblePage, setIsModalVisiblePage] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>('farm_services');
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);

  const [form] = Form.useForm();
  const [formSecond] = Form.useForm();

  const intl = useIntl();

  useEffect(() => {
    if (uploadFileName) {
      form.setFieldValue('logoName', uploadFileName);
    }
  }, [uploadFileName]);

  useQueryApiClient({
    request: {
      url: `api/v2/farm-settings/${selectedEntry}`,
      disableOnMount: !selectedEntry && !isModalVisible,
    },
    onSuccess: (response) => handleOpenFarmSettings(response),
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: selectedEntry
        ? `api/v2/farm-settings/${selectedEntry}?_method=PATCH`
        : `api/v2/farm-settings`,
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => onModalClose(),
  });

  useQueryApiClient({
    request: {
      url: `api/v2/farm-types/${selectedEntry}`,
      disableOnMount: !selectedEntry && !isModalVisiblePage,
    },
    onSuccess: (response) => formSecond.setFieldsValue(response),
  });

  const { appendData: appendDataPage } = useQueryApiClient({
    request: {
      url: selectedEntry
        ? `api/v2/farm-types/${selectedEntry}`
        : `api/v2/farm-types`,
      method: selectedEntry ? 'PATCH' : 'POST',
    },
    onSuccess: () => onModalClose(),
  });

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.id' }),
      dataIndex: 'id',
    },
    {
      title: intl.formatMessage({ id: 'general.name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'settings.description' }),
      dataIndex: 'description',
      render: (value: string) => {
        if (value) {
          return parse(DOMPurify.sanitize(`${value}`));
        }

        return value;
      },
    },
    {
      title: intl.formatMessage({ id: 'general.logo' }),
      dataIndex: 'logo',
    },
    {
      title: intl.formatMessage({ id: 'general.color' }),
      dataIndex: 'color',
      render: (value: string) =>
        value && (
          <ColorPicker value={value} disabled className={'color-picker'} />
        ),
    },
    {
      title: intl.formatMessage({ id: 'general.icon' }),
      dataIndex: 'icon',
    },
    {
      title: intl.formatMessage({ id: 'general.search_url' }),
      dataIndex: 'search_url',
    },
    {
      title: intl.formatMessage({ id: 'general.hide' }),
      dataIndex: 'hidden',
    },
    {
      title: intl.formatMessage({ id: 'general.created_at' }),
      dataIndex: 'created_at',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'general.updated_at' }),
      dataIndex: 'updated_at',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (value: number) => (
        <>
          <Button
            type="link"
            label={intl.formatMessage({ id: 'general.edit' })}
            onClick={() => {
              setSelectedEntry(value);
              setIsModalVisible(true);
            }}
          />
        </>
      ),
    },
  ];

  const farmTypeColumns = [
    {
      title: intl.formatMessage({ id: 'general.id' }),
      dataIndex: 'id',
    },
    {
      title: intl.formatMessage({ id: 'general.name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'settings.description' }),
      dataIndex: 'description',
    },
    {
      title: intl.formatMessage({ id: 'settings.conflict_id' }),
      dataIndex: 'conflict_id',
    },
    {
      title: intl.formatMessage({ id: 'general.created_at' }),
      dataIndex: 'created_at',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'general.updated_at' }),
      dataIndex: 'updated_at',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (value: number) => (
        <>
          <Button
            type="link"
            label={intl.formatMessage({ id: 'general.edit' })}
            onClick={() => {
              setSelectedEntry(value);
              setIsModalVisiblePage(true);
            }}
          />
        </>
      ),
    },
  ];

  const items = [
    {
      key: 'farm_services',
      label: intl.formatMessage({ id: 'settings.farm_services' }),
      children: (
        <>
          <Table
            url="/api/v2/farm-settings"
            columns={columns}
            disablePagination
            reload={reload}
            className={'custom-table'}
          />
        </>
      ),
    },
    {
      key: 'farm_types',
      label: intl.formatMessage({ id: 'settings.farm_types' }),
      children: (
        <>
          <Table
            url="/api/v2/farm-types"
            columns={farmTypeColumns}
            disablePagination
            reload={reload}
          />
        </>
      ),
    },
  ];

  const onModalClose = () => {
    setIsModalVisible(false);
    setIsModalVisiblePage(false);
    setUploadFileName(null);
    setSelectedEntry(0);
    form.resetFields();
    formSecond.resetFields();
    setReload((old) => old + 1);
  };

  const handleOnChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setUploadFileName(info.file.name);
  };

  const AntdUpload = () => {
    return (
      <StyledCustomSuffix>
        <Form.Item name={'logo'} getValueFromEvent={getFile}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            listType={'picture'}
            showUploadList={false}
            onChange={handleOnChange}
            multiple={false}
          >
            {intl.formatMessage({ id: 'general.upload' })}
          </Upload>
        </Form.Item>
      </StyledCustomSuffix>
    );
  };

  const handleOpenFarmSettings = (response: any) => {
    const path = response.logo;
    if (path) {
      const split = path.split('/');
      const fileName = split[split.length - 1];

      setUploadFileName(fileName);
    }

    form.setFieldsValue(response);
  };

  const getFile = (e: any) => {
    if (Array.isArray(e)) {
      return e[0].originFileObj as Blob;
    }
    return e && (e.fileList[0].originFileObj as Blob);
  };

  const onFinish = (values: any) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key === 'logo') {
        if (value instanceof File) {
          formData.append('logo', value);
        }
        return;
      }

      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else {
          formData.append(key, String(value));
        }
      }
    });

    appendData(formData);
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.catalog_settings' }),
        },
      ]}
    >
      <DefaultLayout.PageContent>
        <Tabs
          tabBarExtraContent={
            <StyledButtonList>
              <Button
                label={intl.formatMessage({ id: 'general.create_new' })}
                type="primary"
                onClick={() => {
                  if (activeKey === 'farm_services') {
                    setIsModalVisible(true);
                  }

                  if (activeKey === 'farm_types') {
                    setIsModalVisiblePage(true);
                  }
                }}
              />
            </StyledButtonList>
          }
          items={items}
          onChange={setActiveKey}
        />

        <StyledUploadLogoModal
          open={isModalVisible}
          onCancel={onModalClose}
          title={intl.formatMessage({ id: 'settings.farm_services' })}
          footer={
            <CustomGrid gapRow={10}>
              <Button
                label={intl.formatMessage({ id: 'general.cancel' })}
                onClick={onModalClose}
              />
              <Button
                label={intl.formatMessage({ id: 'general.submit' })}
                type="primary"
                htmlType="submit"
                onClick={() => form.submit()}
              />
            </CustomGrid>
          }
        >
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Input
              name="name"
              label={intl.formatMessage({ id: 'general.name' })}
              validations={['required']}
              maxLength={255}
            />
            <QuillGlobal />
            <RichTextInput
              name="description"
              label={intl.formatMessage({ id: 'settings.description' })}
            />
            <Input
              name={'logoName'}
              label={intl.formatMessage({ id: 'general.logo' })}
              suffix={<AntdUpload />}
              disabled
            />
            <ColorPicker
              name="color"
              label={intl.formatMessage({ id: 'general.color' })}
            />
            <Input
              name="icon"
              label={intl.formatMessage({ id: 'general.icon' })}
              maxLength={255}
            />
            <Input
              name="search_url"
              label={intl.formatMessage({ id: 'general.search_url' })}
              maxLength={255}
            />
            <Checkbox
              name="hidden"
              label={intl.formatMessage({ id: 'general.hide' })}
            />
          </Form>
        </StyledUploadLogoModal>

        <Modal
          open={isModalVisiblePage}
          onCancel={onModalClose}
          title={intl.formatMessage({ id: 'settings.farm_types' })}
          footer={
            <CustomGrid gapRow={10}>
              <Button
                label={intl.formatMessage({ id: 'general.cancel' })}
                onClick={onModalClose}
              />
              <Button
                label={intl.formatMessage({ id: 'general.submit' })}
                type="primary"
                htmlType="submit"
                onClick={() => formSecond.submit()}
              />
            </CustomGrid>
          }
        >
          <Form form={formSecond} onFinish={appendDataPage} layout="vertical">
            <Input
              name="name"
              label={intl.formatMessage({ id: 'general.name' })}
              validations={['required']}
              maxLength={255}
            />
            <QuillGlobal />
            <RichTextInput
              name="description"
              label={intl.formatMessage({ id: 'settings.description' })}
            />
            <Input
              name="conflict_id"
              label={intl.formatMessage({ id: 'settings.conflict_id' })}
              maxLength={255}
            />
          </Form>
        </Modal>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default SettingListPage;
